import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { spacing } from 'syf-component-library/defaultTheme';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import { Inset } from 'syf-component-library/ui/spacing';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE, MESSAGE_TYPES, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import MULTI_PAYMENT_FLOW_TABS from '../../../../const/multiPaymentFlowTabs';
import MainContext from '../../../../Context';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { MakeAPaymentView, useCurrentViewAtom } from '../../../../store/currentViewAtom';
import { t } from '../../../../strings/i18n';
import ClickableTab from '../../../../ui/atoms/ClickableTab';
import PaymentStepper from '../../../../ui/molecules/PaymentModalStates/PaymentFormSubcomponents/PaymentStepper';
import SettlementMessageBox from '../../../../ui/molecules/SettlementMessageBox';
import Tabs from '../../../../ui/molecules/Tabs';
import XStateCore from '../../XStateCore';
import MultiPaymentFlow from '.';
const MultiPaymentFlowTab = styled(ClickableTab) `
  color: #34657f;
  font-weight: bold;
  padding-left: ${(props) => (props.index === 1 ? '16px' : '0')};
`;
const TabsWrapper = styled(Tabs) `
  margin: ${spacing.xlarge}px ${spacing.large}px 0 ${spacing.large}px;
  border-bottom: 1px solid #e0e0e0;

  // Added to ensure footer stays fixed when wrapping XStateCore
  ~ section {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;
const MultiPaymentDetails = ({ headerRef, mainService, handleTabSwitch, handleCancel }) => {
    const { multiPaymentFlowCurrentTab, triggerAnalyticsEvent } = useContext(MainContext);
    const [, oneTimePaymentActor] = usePaymentFlowMachine();
    const { handleSubmit } = useFormContext();
    const [currentViewAtom, currentViewReducer] = useCurrentViewAtom();
    /*
     * If you are in the multi payment flow, editing in one time flow, or editing in multi payment flow
     * you need to disable the analytics in SettlementMessageBox
     */
    const shouldDisableOneTimeStartAnalytics = multiPaymentFlowCurrentTab === MULTI_PAYMENT_FLOW_TABS.MULTI ||
        oneTimePaymentActor.event.type === 'EDIT' ||
        currentViewAtom.view === MakeAPaymentView.MultiPaymentDetailsEdit;
    const handleFormSubmit = () => {
        currentViewReducer({
            type: 'updateView',
            view: MakeAPaymentView.MultiPaymentReview
        });
    };
    const handleFormError = (errors) => {
        const firstErrorName = Object.values(Object.values(errors)[0])[0].ref.name;
        const firstErrorIndex = firstErrorName.split('.')[0];
        const section = document.querySelector(`[data-scroll="multi-payment-${firstErrorIndex}"]`);
        section.scrollIntoView();
    };
    useEffect(() => {
        if (currentViewAtom.view === MakeAPaymentView.MultiPaymentDetailsEdit) {
            const eventOptionsV1 = {
                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                messageType: MESSAGE_TYPES.DCH.SETTLEMENT_MULTI,
                pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.MULTI_PAYMENT,
                pageName: PAGE_NAME.MULTI_PAYMENT.EDIT
            };
            triggerAnalyticsEvent(eventOptionsV1);
        }
        else if (multiPaymentFlowCurrentTab === MULTI_PAYMENT_FLOW_TABS.MULTI) {
            const eventOptionsV1 = {
                messageType: MESSAGE_TYPES.DCH.SETTLEMENT_MULTI,
                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.MULTI_PAYMENT,
                pageName: PAGE_NAME.START
            };
            triggerAnalyticsEvent(eventOptionsV1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiPaymentFlowCurrentTab]);
    return (_jsxs(_Fragment, { children: [_jsx(PaymentStepper, { step: "details" }), _jsx(Inset, Object.assign({ top: "large", vertical: "large" }, { children: _jsx(SettlementMessageBox, { shouldDisableStartAnalytics: shouldDisableOneTimeStartAnalytics }) })), _jsxs(TabsWrapper, Object.assign({ currentTab: multiPaymentFlowCurrentTab, defaultTab: multiPaymentFlowCurrentTab, onTabClick: handleTabSwitch }, { children: [_jsx(MultiPaymentFlowTab, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-object": DATA_OBJECT.TAB, "data-reason": DATA_REASON.MULTI_PAYMENT.TABS.ONE_TIME, "data-type": DATA_TYPE.MAKE_A_PAYMENT, id: "one-time-payment-tab", index: MULTI_PAYMENT_FLOW_TABS.ONE_TIME, label: t('multiPaymentFlow.oneTimePaymentTabTitle').toUpperCase(), onTabClick: () => {
                            handleTabSwitch(MULTI_PAYMENT_FLOW_TABS.ONE_TIME);
                            currentViewReducer({
                                type: 'updateView',
                                view: MakeAPaymentView.MultiPaymentDetails
                            });
                        } }, { children: _jsx(XStateCore, { headerRef: headerRef, mainService: mainService }) })), _jsx(MultiPaymentFlowTab, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "multi-payment-tab", "data-object": DATA_OBJECT.TAB, "data-reason": DATA_REASON.MULTI_PAYMENT.TABS.MULTI_PAYMENT, "data-type": DATA_TYPE.MAKE_A_PAYMENT, id: "multi-payment-tab", index: MULTI_PAYMENT_FLOW_TABS.MULTI, label: t('multiPaymentFlow.multiPaymentTabTitle').toUpperCase(), onTabClick: () => handleTabSwitch(MULTI_PAYMENT_FLOW_TABS.MULTI) }, { children: _jsx(Inset, Object.assign({ all: "large" }, { children: _jsx("form", Object.assign({ onSubmit: handleSubmit(handleFormSubmit) }, { children: _jsx(MultiPaymentFlow, {}) })) })) }))] })), multiPaymentFlowCurrentTab === MULTI_PAYMENT_FLOW_TABS.MULTI && (_jsx(FooterContainer, { children: _jsx(ModalFooter, { dataCy: "payment-details-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: DATA_REASON.MULTI_PAYMENT.NEXT, dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: DATA_REASON.MULTI_PAYMENT.CANCEL, dataType: DATA_TYPE.MAKE_A_PAYMENT, handlePrimaryButton: handleSubmit(handleFormSubmit, handleFormError), handleSecondaryButton: handleCancel, primaryText: t('button.next'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) }))] }));
};
export default MultiPaymentDetails;
